import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { UI_LANGUAGES } from '@bienlivrer/config';

type Variant = 'pill' | 'default' | 'compact' | 'touch';

type Props = { variant?: Variant };

const LANG_LABEL: Record<(typeof UI_LANGUAGES)[number], string> = {
  fr: 'Langue',
  de: 'Sprache',
  en: 'Language',
  it: 'Lingua',
};

export default function LanguageSwitcher({ variant = 'default' }: Props) {
  const { i18n } = useTranslation();
  const langActive = (code: (typeof UI_LANGUAGES)[number]) =>
    i18n.language.startsWith(code);
  const current = UI_LANGUAGES.find(langActive) ?? 'fr';
  const aria = LANG_LABEL[current];

  if (variant === 'compact') {
    return <CompactSwitcher current={current} aria={aria} langActive={langActive} />;
  }

  const wrap =
    variant === 'pill'
      ? 'flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl'
      : 'flex flex-wrap gap-1';
  const btnBase =
    variant === 'touch'
      ? 'min-h-11 min-w-11 touch-manipulation px-2.5 rounded-xl text-xs font-semibold transition-colors'
      : 'min-h-11 min-w-11 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors';
  const activeCls =
    variant === 'touch'
      ? 'bg-primary-700 text-white'
      : 'bg-white text-primary-700 shadow-sm';
  const idleCls =
    variant === 'touch'
      ? 'bg-gray-100 text-gray-600 active:bg-gray-200'
      : 'text-gray-500 hover:text-gray-700';

  return (
    <div className={wrap} role="group" aria-label={aria}>
      {UI_LANGUAGES.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => void i18n.changeLanguage(l)}
          className={`${btnBase} ${langActive(l) ? activeCls : idleCls}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function CompactSwitcher({
  current,
  aria,
  langActive,
}: {
  current: (typeof UI_LANGUAGES)[number];
  aria: string;
  langActive: (code: (typeof UI_LANGUAGES)[number]) => boolean;
}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={aria}
        className="inline-flex items-center justify-center gap-0.5 min-h-11 min-w-11 px-2 rounded-xl bg-gray-100 text-xs font-semibold text-gray-800 hover:bg-gray-200 transition-colors"
      >
        {current.toUpperCase()}
        <ChevronDown size={14} aria-hidden className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={aria}
          className="absolute right-0 top-full mt-1 z-50 min-w-[8.5rem] bg-white border border-gray-200 rounded-xl shadow-xl py-1"
        >
          {UI_LANGUAGES.map((l) => (
            <button
              key={l}
              type="button"
              role="option"
              aria-selected={langActive(l)}
              onClick={() => {
                void i18n.changeLanguage(l);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2.5 text-sm font-semibold min-h-11 ${
                langActive(l) ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
